import React, { useCallback, useEffect, useRef, useState } from "react";

import { Box, useBreakpointValue } from "@chakra-ui/react";

import { NavigationBar } from "../../components/NavigationBar";

import {
	ChoosePhoneStep,
	EntryCPFStep,
	InsertPhoneStep,
	TokenStep,
} from "./Steps";
import {
	ExceedAttemptsFeedback, IsNotPhoneOwnerFeedback, NotFoundFeedback, OpenAccountsFeedback,
} from "./Steps/Feedbacks";
import { useGlobalStore } from "../../contexts/globalStoreContext";
import { useNavigateCustom } from "../../hooks";
import { observer } from "mobx-react-lite";
import { getFFIsDisableFGTS } from "../../services/SystemConfiguration";
import { LoadingContainer } from "../../components";


const Authentication: React.FC = () => {
	const isMobile = useBreakpointValue({ base: true, md: false });
	const navigate = useNavigateCustom();
	const [loading, setLoading] = useState(true);
	const firstRenderRef = useRef(true);

	const { authenticationStore: {
		onBackPress,
		sendSMSToken,
		fetchSendCPF,
		signOtpValidation,
		onChooseAnotherPhoneOnTokenStepsClick,
		cpf,
		steps,
		loader,
		showNavBar,
		selectedPhone,
		showBackButton,
		userEligibility,
		currentPhoneIndex,
		cpfValidationError,
		tokenValidationError,
		mustShowAlterLineModal,
		getIsCheckNotification,
	} } = useGlobalStore();

	const toggleOverflow = React.useCallback(() => {
		const element = document.querySelector("body");
		if (element) {
			element.style.overflow = "auto";
		}
	}, []);

	React.useEffect(() => {
		toggleOverflow();
	}, [toggleOverflow]);

	const handleGetFF = async (): Promise<void> => {
		try {
			setLoading(true);
			const isDisableFGTS = await getFFIsDisableFGTS();

			if (isDisableFGTS) {
				navigate("/fgts-indisponivel", { replace: true });
			}
		} catch (error) {
			console.error("Error fetching feature flag:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (firstRenderRef.current) {
			firstRenderRef.current = false;
			handleGetFF();
		}
	}, []);

	const renderStep = useCallback(() => {
		switch (steps.getLastOne) {
			default:
			case "cpf":
				return (
					<EntryCPFStep
						isLoading={loader.isLoading}
						hasCPFError={cpfValidationError.value}
						cleanCPFError={() => cpfValidationError.set(false)}
						onSubmit={async (cpfUser) => {
							await fetchSendCPF(cpfUser,	isMobile || false, navigate);
						}}
					/>
				);
			case "choosePhone":
				return (
					<ChoosePhoneStep
						phoneNumbers={userEligibility.value?.phones || []}
						currentPhoneIndex={currentPhoneIndex.value}
						onSubmit={async (phone) => {
							await sendSMSToken(phone, false, navigate);
						}}
						isLoading={loader.isLoading}
						mustShowAlterLineModal={mustShowAlterLineModal.value}
					/>
				);
			case "unavailableCredit":
				steps.setValue([]);
				return navigate("/customer-refusal", { replace: true });
			case "openAccounts":
				return <OpenAccountsFeedback />;
			case "noFinance":
				steps.setValue([]);
				return navigate("/customer-refusal", { replace: true });
			case "expiredDeal":
				navigate("/emprestimo-simulacao");
				return;
			case "notFound":
				return <NotFoundFeedback />;
			case "token":
				return (
					<TokenStep
						isLoading={loader.isLoading}
						userHasAcceptedPreviouslyToReceiveNotification={getIsCheckNotification}
						cellphone={selectedPhone.value}
						onResendCodeClick={() => {
							sendSMSToken(selectedPhone.value, false, navigate);
						}}
						onValidateTokenClick={(token) => {
							signOtpValidation(token, cpf.value, navigate);
						}}
						onLoginAnotherNumberClick={() => {
							onChooseAnotherPhoneOnTokenStepsClick();
						}}
						validationError={tokenValidationError.value}
						showChooseAnotherNumberButton={
							userEligibility.value!.phones.length > 1
						}
						resetError={() => tokenValidationError.set(null)}
					/>
				);
			case "insertPhone":
				return (
					<InsertPhoneStep
						isLoading={loader.isLoading}
						onSubmit={async (phone) => {
							await sendSMSToken(
								phone,
								true,
								navigate,
							);
						}}
					/>
				);
			case "exceedAttempts":
				return <ExceedAttemptsFeedback />;
			case "isNotPhoneOwner":
				return (
					<IsNotPhoneOwnerFeedback
						onChangeInfoClick={() => steps.addOnEnd("cpf")}
					/>
				);
		}
	}, [steps.getLastOne]);

	// Loading full page while fetching feature flag
	if (loading) {
		return (
			<Box w="100vw" h="100vh" display="flex" justifyContent="center" alignItems="center">
				<LoadingContainer title="" />
			</Box>
		);
	}

	return (
		<Box>
			<>
				<NavigationBar
					onBack={() =>
						showBackButton.value ? onBackPress(navigate) : undefined
					}
					showBar={showNavBar.value}
				/>
				{renderStep()}
			</>
		</Box>
	);
};

export default observer(Authentication);
